/**
 * budget-state — the ONE derivation of a cumulative cap's live utilization state, shared by every
 * surface that shows "how much of an agent's standing allowance is left before it must come back and
 * ask you". Framework-agnostic and UNIT-AGNOSTIC: a cap is a number in a dimension (usd, records,
 * gpu_hours, iocs), and every figure is rendered through the dimension's own DimMeta — there is NO
 * money branch, no `$` constant, no currency assumption in the state logic.
 *
 * Before this module the fraction/threshold/humanization lived inline in three places (the portal
 * access page, the portal `cumulativeBudget` helper, the admin standing-delegations table), each with
 * its own literal `0.8` — so "approaching" could mean three different things. This is the single home
 * of that threshold and of the used/left/max humanization, so the meter reads identically everywhere.
 *
 * "used" comes from the SAME ledger the enforcement gate reserves against, so the state here is
 * authoritative, not an approximation.
 */

import { formatValue, type DimMeta } from "./consequence-format";
import { currencyDimMeta, isKnownCurrencyCode } from "./unit-catalog";

/** Warn once the consumed fraction reaches this — the "nearly used up" band. Also fires value-aware. */
export const BUDGET_APPROACHING_FRACTION = 0.8;

/**
 * One cumulative cap's live utilization, as every surface already reads it off the wire
 * (`standing.lifetime_utilization[]` in the portal, the admin table's equivalent). All magnitudes are
 * in the dimension's SMALLEST units (the ledger's storage unit); `exponent`/`unitCode`/`displayName`
 * are the dimension's display metadata so the figure renders in its real unit. `perActionMax` (the
 * per-action cap on the same dimension, smallest units) is optional and drives the value-aware
 * "approaching" test — when the next single autonomous action could exhaust what's left.
 */
export interface BudgetUtilizationInput {
  dimension: string;
  used: number; // SETTLED spend
  // In-flight escrow holds on the same enforced anchor (committed, not yet settled). The gate enforces
  // used + held, so the headroom and "approaching" level must count both. Optional/0 where escrow is off.
  held?: number;
  max: number;
  windowKind?: string;
  exponent?: number;
  unitCode?: string;
  displayName?: string;
  perActionMax?: number | null;
}

/**
 * The utilization state, ordered by severity. `under` — comfortably below; `approaching` — at/over the
 * warn fraction OR the next per-action could exhaust it; `used_up` — the cap is reached, so every
 * further value-bearing action stops and asks the human (escalate) until the window resets/ends.
 */
export type BudgetLevel = "under" | "approaching" | "used_up";

export interface BudgetState {
  dimension: string;
  level: BudgetLevel;
  /** 0..1 consumed fraction, clamped — for the meter bar (fill = consumed toward the ceiling). */
  fraction: number;
  usedText: string;
  leftText: string;
  maxText: string;
  /** True at exactly zero consumed — lets a caller say "Full X available" instead of "X left of X". */
  fresh: boolean;
  /** Plain-language reset horizon, e.g. "until this access ends" / "resets at the start of each month".
   *  Empty string for an unknown/absent window (the caller then omits the clause). */
  horizon: string;
}

/**
 * dimMetaForBudget resolves a dimension's display metadata for a utilization row: a known ISO 4217
 * currency uses the catalog's authoritative symbol + exponent; every other dimension uses the exponent,
 * unit, and name the WIRE carries. This is the currency-symbol handling that already lives in the shared
 * humanizer (currencyDimMeta) — NOT a money branch in the product logic. Exported so every caller that
 * HAS a utilization row's wire metadata (the portal budget derivation, the admin table) resolves a
 * dimension identically, collapsing their old per-feature copies. (The grant-form setter keeps a thinner
 * key-only resolver because at authoring time it has only the dimension key, no wire exponent/unit.)
 */
export function dimMetaForBudget(u: {
  dimension: string;
  exponent?: number;
  unitCode?: string;
  displayName?: string;
}): DimMeta {
  if (isKnownCurrencyCode(u.dimension)) return currencyDimMeta(u.dimension);
  return { displayName: u.displayName || u.dimension, exponent: u.exponent ?? 0, unitCode: u.unitCode };
}

/** budgetHorizon renders a cumulative window as end-user language (never the raw enum). "until this
 *  access ends" vs "resets monthly" are opposite feelings for the same "X left" number. */
export function budgetHorizon(windowKind: string | undefined): string {
  switch (windowKind) {
    case "delegation_lifetime":
      return "until this access ends";
    case "calendar_month":
      return "resets at the start of each month";
    case "calendar_day":
      return "resets each day";
    case "rolling":
      return "over a rolling window";
    default:
      return "";
  }
}

/**
 * deriveBudgetState turns ONE cumulative cap's utilization into its display state. Unit-agnostic: the
 * used/left/max text is `formatValue` in the dimension's real unit. "approaching" fires when the
 * consumed fraction reaches BUDGET_APPROACHING_FRACTION OR — value-aware — when the remaining headroom
 * is smaller than a single per-action cap (so the very next autonomous action could exhaust it). A
 * degenerate `max <= 0` is treated as fully consumed (deny-all), guarding the divide.
 */
export function deriveBudgetState(u: BudgetUtilizationInput): BudgetState {
  const meta = dimMetaForBudget(u);
  const fmt = (q: number) => formatValue(q, u.dimension, meta);
  // The gate enforces used + held, so the headroom and level must count BOTH the settled spend and the
  // in-flight escrow holds (P3). held defaults 0 where escrow is off, preserving the pre-escrow behavior.
  const held = u.held ?? 0;
  const committed = u.used + held;
  const left = Math.max(0, u.max - committed);
  const fraction = u.max > 0 ? Math.min(1, Math.max(0, committed / u.max)) : 1;
  const usedUp = left <= 0;
  // Value-aware approaching: a single per-action cap larger than the remaining headroom means the next
  // autonomous action alone could cross the total — warn even if the fraction still looks calm.
  const perActionCouldExhaust =
    u.perActionMax != null && u.perActionMax > 0 && left < u.perActionMax;
  const level: BudgetLevel = usedUp
    ? "used_up"
    : fraction >= BUDGET_APPROACHING_FRACTION || perActionCouldExhaust
      ? "approaching"
      : "under";
  return {
    dimension: u.dimension,
    level,
    fraction,
    usedText: fmt(u.used),
    leftText: fmt(left),
    maxText: fmt(u.max),
    fresh: committed <= 0,
    horizon: budgetHorizon(u.windowKind),
  };
}

/**
 * deriveBudgetStates derives one state PER capped dimension, in wire order. This replaces the old
 * "prefer a currency, pick one, drop the rest" reduction (which silently hid a records/gpu budget when
 * a usd budget was also set). Every cap the grant carries gets its own state, so no surface can under-
 * report what it checked. Returns [] when the grant has no cumulative cap.
 */
export function deriveBudgetStates(utils: readonly BudgetUtilizationInput[] | undefined): BudgetState[] {
  return (utils ?? []).map(deriveBudgetState);
}
